import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Box,
} from '@mui/material';
import { FC, useCallback, useMemo, useState, useEffect } from 'react';
import { SurveyPayloadDto, SurveyPayloadFrequencyConfigDto, SurveyPayloadSurveyPeriodConfig } from '../../../dtos';
import { useUpdateSurveyFrequencyMutation } from '../../../store';
import { useGetScheduleQuery } from '../../../store/api/schedule-api';
import { StandardDialogHeader, useFailedActionSnackbar, useSuccessfulActionSnackbar } from '../../CoreLib/library';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { FrequencySurveyPeriod } from '../../surveyStepper/steps/SubComponents/FrequencySurveyPeriod';
import _ from 'lodash';

interface IUpdateSurveyFrequencyModalProps {
    scheduleId: string;
    open: boolean;
    onClose: () => void;
}

export const UpdateSurveyFrequencyModal: FC<IUpdateSurveyFrequencyModalProps> = ({ scheduleId, open, onClose }) => {
    const { data: schedule, isLoading: isLoadingSchedule, isError: isErrorLoadingSchedule } = useGetScheduleQuery(scheduleId);

    const surveyId = useMemo(() => schedule?.surveyId || '', [schedule?.surveyId]);

    const currentFrequencyConfig = useMemo<SurveyPayloadFrequencyConfigDto | null>(() => {
        try {
            if (schedule?.survey?.surveyPayload) {
                const payload: SurveyPayloadDto = JSON.parse(schedule.survey.surveyPayload);
                return payload.frequencyConfig || null;
            }
        } catch (error) {
            console.error('Failed to parse survey payload:', error);
        }
        return null;
    }, [schedule?.survey?.surveyPayload]);

    const [frequencyValue, setFrequencyValue] = useState<string>('');
    const [surveyPeriods, setSurveyPeriods] = useState<SurveyPayloadSurveyPeriodConfig[]>([]);

    useEffect(() => {
        if (currentFrequencyConfig) {
            setFrequencyValue(currentFrequencyConfig.frequencyValue || '');
            setSurveyPeriods(currentFrequencyConfig.surveyPeriods ? _.cloneDeep(currentFrequencyConfig.surveyPeriods) : []);
        }
    }, [currentFrequencyConfig]);

    useEffect(() => {
        if (!open) {
            if (currentFrequencyConfig) {
                setFrequencyValue(currentFrequencyConfig.frequencyValue || '');
                setSurveyPeriods(currentFrequencyConfig.surveyPeriods ? _.cloneDeep(currentFrequencyConfig.surveyPeriods) : []);
            }
        }
    }, [open, currentFrequencyConfig]);

    const [updateSurveyFrequency, {
        isLoading: isUpdatingFrequency,
        isSuccess: isUpdateFrequencySuccessful,
        isError: isUpdateFrequencyError
    }] = useUpdateSurveyFrequencyMutation();

    useSuccessfulActionSnackbar('update', 'survey frequency', isUpdateFrequencySuccessful, () => {
        onClose();
    });

    useFailedActionSnackbar('update', 'survey frequency', isUpdateFrequencyError);

    const handleFrequencyChange = (event: SelectChangeEvent) => {
        const newFrequency = event.target.value;
        setFrequencyValue(newFrequency);
        
        if (newFrequency === 'every-year') {
            setSurveyPeriods(prev => prev.length > 0 ? [prev[0]] : [createEmptyPeriodConfig()]);
        } else if (newFrequency === 'every-three-months') {
            const periods = [...surveyPeriods];
            while (periods.length < 4) {
                periods.push(createEmptyPeriodConfig());
            }
            setSurveyPeriods(periods.slice(0, 4));
        }
    };

    const createEmptyPeriodConfig = (): SurveyPayloadSurveyPeriodConfig => {
        return {
            month: '',
            day: '',
            periodStartMonth: undefined,
            periodStartDay: undefined,
            periodEndMonth: undefined,
            periodEndDay: undefined,
            surveyLabelPrefix: '',
            yearLabel: 'current-year',
            surveyLabelSuffix: '',
        };
    };

    const updateSurveyConfig = (config: SurveyPayloadSurveyPeriodConfig, index: number) => {
        const updatedPeriods = [...surveyPeriods];
        updatedPeriods[index] = config;
        setSurveyPeriods(updatedPeriods);
    };

    const handleUpdateFrequency = useCallback(async () => {
        if (!surveyId || !frequencyValue || surveyPeriods.length === 0) return;

        await updateSurveyFrequency({
            surveyId,
            scheduleId,
            frequencyValue,
            surveyPeriods,
        });
    }, [surveyId, scheduleId, frequencyValue, surveyPeriods, updateSurveyFrequency]);

    const isPeriodConfigValid = useCallback((config: SurveyPayloadSurveyPeriodConfig): boolean => {
        return (
            config.periodStartMonth !== undefined &&
            config.periodStartDay !== undefined &&
            config.periodEndMonth !== undefined &&
            config.periodEndDay !== undefined &&
            config.month !== '' &&
            config.day !== ''
        );
    }, []);

    const isValid = useMemo(() => {
        if (!frequencyValue || surveyPeriods.length === 0) return false;
        
        if (frequencyValue === 'every-year') {
            return isPeriodConfigValid(surveyPeriods[0]);
        } else if (frequencyValue === 'every-three-months') {
            return surveyPeriods.length === 4 && surveyPeriods.every(isPeriodConfigValid);
        }
        return false;
    }, [frequencyValue, surveyPeriods, isPeriodConfigValid]);

    const errorMessage = useMemo(() => {
        if (isLoadingSchedule) {
            return null;
        }
        if (isErrorLoadingSchedule) {
            return "Failed to load schedule information.";
        }
        if (!currentFrequencyConfig) {
            return "Unable to load survey frequency configuration.";
        }
        if (!currentFrequencyConfig.radioOneTimeOrRecurring || currentFrequencyConfig.radioOneTimeOrRecurring !== 'recurring') {
            return "This survey is not recurring. Frequency can only be modified for recurring surveys.";
        }
        if (!isValid && frequencyValue) {
            return "All period start and end dates are required for each occurrence.";
        }
        return null;
    }, [isLoadingSchedule, isErrorLoadingSchedule, currentFrequencyConfig, isValid, frequencyValue]);

    const handleFrequencySurveyPeriodComponents = () => {
        if (frequencyValue === 'every-year') {
            if (surveyPeriods.length === 0) {
                return null;
            }
            return (
                <FrequencySurveyPeriod
                    surveyPeriodConfig={surveyPeriods[0]}
                    updateSurveyConfig={updateSurveyConfig}
                    surveyPeriodIndex={0}
                    frequency={frequencyValue}
                />
            );
        } else if (frequencyValue === 'every-three-months') {
            if (surveyPeriods.length < 4) {
                return null;
            }
            return (
                <>
                    <FrequencySurveyPeriod
                        surveyPeriodConfig={surveyPeriods[0]}
                        updateSurveyConfig={updateSurveyConfig}
                        surveyPeriodIndex={0}
                        frequency={frequencyValue}
                    />
                    <FrequencySurveyPeriod
                        surveyPeriodConfig={surveyPeriods[1]}
                        updateSurveyConfig={updateSurveyConfig}
                        surveyPeriodIndex={1}
                        frequency={frequencyValue}
                    />
                    <FrequencySurveyPeriod
                        surveyPeriodConfig={surveyPeriods[2]}
                        updateSurveyConfig={updateSurveyConfig}
                        surveyPeriodIndex={2}
                        frequency={frequencyValue}
                    />
                    <FrequencySurveyPeriod
                        surveyPeriodConfig={surveyPeriods[3]}
                        updateSurveyConfig={updateSurveyConfig}
                        surveyPeriodIndex={3}
                        frequency={frequencyValue}
                    />
                </>
            );
        }
        return null;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <StandardDialogHeader
                icon={<ScheduleIcon fontSize='inherit' />}
                text="Update Survey Frequency"
            />

            <DialogContent>
                <Box pt={2}>
                    {isLoadingSchedule ? (
                        <Typography>Loading schedule information...</Typography>
                    ) : isErrorLoadingSchedule || !currentFrequencyConfig ? (
                        <Typography color="error">Unable to load schedule information.</Typography>
                    ) : (
                        <Grid container direction="column" spacing={3}>
                            <Grid item>
                                <Alert severity="info" variant='outlined'>
                                    <Typography variant="body2">
                                        <strong>Important:</strong> Updating the survey frequency will cancel all future unissued schedules 
                                        and create a new schedule based on the updated frequency configuration.
                                    </Typography>
                                </Alert>
                            </Grid>

                            <Grid item container direction="column" spacing={2}>
                                <Grid item>
                                    <FormControl fullWidth>
                                        <InputLabel>Frequency</InputLabel>
                                        <Select
                                            value={frequencyValue}
                                            onChange={handleFrequencyChange}
                                            label="Frequency"
                                            disabled={isUpdatingFrequency}
                                        >
                                            <MenuItem value={'every-year'}>Every year</MenuItem>
                                            <MenuItem value={'every-three-months'}>Every 3 months</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {frequencyValue && (
                                <Grid item>
                                    <Typography variant='h6' sx={{ marginBottom: '16px' }}>
                                        What dates should the survey be sent and what time period should they cover?
                                    </Typography>
                                    <Grid item xs={12}>
                                        <Table size='small' padding='none'>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 1 }}>Occurrence</TableCell>
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 1, pl: 2, background: '#eeeeee' }}>Send Month</TableCell>
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 2, background: '#eeeeee' }}>Day</TableCell>
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 2 }} />
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 1, pl: 2, background: '#eeeeee' }}>Period Start Month</TableCell>
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 1, background: '#eeeeee' }}>Day</TableCell>
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 1, background: '#eeeeee' }}>Period End Month</TableCell>
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 2, background: '#eeeeee' }}>Day</TableCell>
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 2 }} />
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 1, pl: 2, background: '#eeeeee' }}>Label Prefix</TableCell>
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 1, background: '#eeeeee' }}>Year Label</TableCell>
                                                    <TableCell sx={{ py: 1, border: 'none', pr: 2, background: '#eeeeee' }}>Label Suffix</TableCell>
                                                    <TableCell sx={{ py: 1, border: 'none', pl: 1 }} width={240}>
                                                        Example
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {handleFrequencySurveyPeriodComponents()}
                                            </TableBody>
                                        </Table>
                                    </Grid>
                                </Grid>
                            )}

                            {errorMessage && (
                                <Grid item>
                                    <Typography variant="body1" sx={{ color: '#DF623B' }}>
                                        {errorMessage}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={isUpdatingFrequency} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleUpdateFrequency}
                    variant="contained"
                    disabled={!isValid || isUpdatingFrequency}
                    sx={{ width: '196px !important' }}
                >
                    {isUpdatingFrequency ? 'Updating...' : 'Update Frequency'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
